'use client';

import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import ProfileHeader from './components/ProfileHeader';
import ProfilePerformanceChart from './components/ProfilePerformanceChart';
import ProfileTabs from './components/ProfileTabs';
import ProfileSkeleton from './components/ProfileSkeleton';
import PartnerHero from '@/components/common/PartnerHero';
import AcademyBanner from '@/components/common/AcademyBanner';
import { ArrowLeft, UserPlus, TrendingUp, ShieldCheck } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import type { PublicProfile } from '@/lib/users/getPublicProfile';

// What /api/users/[username] returns as `profile` (+ the XP fields merged in for the owner).
export type ProfileData = PublicProfile & {
  xp?: number;
  level?: number;
  streakDays?: number;
};

interface MarketFocusItem {
  market: string;
  pct: number;
}

const MARKET_COLORS: Record<string, string> = {
  FOREX: 'var(--accent)',
  CRYPTO: 'var(--win)',
  INDICES: 'var(--info)',
  COMMODITIES: 'var(--bearish)',
  STOCKS: 'var(--bullish)',
};

// The whole profile view (header, performance, tabs, sidebar). Used by:
//   • /user-profile?username=<handle>  — legacy URL; `username` comes from the query string
//   • /profile/[username]              — server page passes `username` + `initialProfile` (an
//                                        anonymous read of the same payload) so the header is in
//                                        the SSR HTML; the API refetch on mount then fills in the
//                                        viewer-specific bits (isFollowing, isCurrentUser, contact).
export default function ProfileView({
  username,
  initialProfile,
}: {
  username?: string;
  initialProfile?: ProfileData | null;
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user: currentUser, session, loading: authLoading } = useAuth();
  const { t } = useLang();
  const usernameParam = username ?? searchParams.get('username');

  const [loading, setLoading] = useState(!initialProfile);
  const [profile, setProfile] = useState<ProfileData | null>(initialProfile ?? null);
  const [marketFocus, setMarketFocus] = useState<MarketFocusItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [achievements, setAchievements] = useState<Array<{ achievement: string; unlockedAt: string }>>([]);
  const [demo, setDemo] = useState<{ account: any | null; scores: any | null; demoStats?: any } | null>(null);

  useEffect(() => {
    async function loadProfile() {
      // Wait for auth to finish loading before making decisions
      if (authLoading) return;

      // Server-seeded header stays on screen while the session-aware refetch runs — no skeleton.
      if (!initialProfile) setLoading(true);
      setError(null);
      setIsPrivate(false);

      try {
        let targetUsername = usernameParam;

        // If no username param, try to get current user's profile
        if (!targetUsername && currentUser) {
          // Try /api/settings/profile first (has GET handler)
          const res = await fetch('/api/settings/profile', { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            targetUsername = data.profile?.username || null;
          }
          // Fallback: try /api/settings
          if (!targetUsername) {
            const settingsRes = await fetch('/api/settings', { credentials: 'include' });
            if (settingsRes.ok) {
              const settingsData = await settingsRes.json();
              targetUsername = settingsData.settings?.username || null;
            }
          }
        }

        // Not logged in and no username specified → redirect to login
        if (!targetUsername && !currentUser) {
          router.push('/sign-up-login-screen');
          return;
        }

        if (!targetUsername) {
          setError(t('Could not load your profile. Please try signing out and back in.'));
          setLoading(false);
          return;
        }

        const profileRes = await fetch(`/api/users/${encodeURIComponent(targetUsername)}`);
        if (!profileRes.ok) {
          const err = await profileRes.json().catch(() => ({}));
          if (profileRes.status === 403 && err.private) {
            setIsPrivate(true);
            setError(t('This trader has set their profile to private.'));
          } else {
            setError(err.error || t('Failed to load profile'));
          }
          setLoading(false);
          return;
        }
        const profileData = await profileRes.json();
        setProfile(profileData.profile);

        const perfRes = await fetch(`/api/users/performance?userId=${profileData.profile.userId}`);
        if (perfRes.ok) {
          const perf = await perfRes.json();
          setMarketFocus(perf.marketFocus || []);
        }

        // Fetch XP/achievements for current user's own profile
        if (profileData.profile.isCurrentUser) {
          try {
            const xpRes = await fetch('/api/xp', { credentials: 'include' });
            if (xpRes.ok) {
              const xpData = await xpRes.json();
              setAchievements(xpData.achievements ?? []);
              setProfile((prev) => prev ? {
                ...prev,
                xp: xpData.xp ?? 0,
                level: xpData.level ?? 1,
                streakDays: xpData.streakDays ?? 0,
              } : prev);
            }
          } catch {}
        }

        // Load the simulated (demo) account, stats and scores for THIS profile (public — by userId,
        // same convention as /api/users/performance). Shows demo equity on any trader's profile.
        try {
          const demoHeaders: Record<string, string> = {};
          if (session?.access_token) demoHeaders['Authorization'] = `Bearer ${session.access_token}`;
          const demoRes = await fetch(`/api/demo/account?userId=${encodeURIComponent(profileData.profile.userId)}`, {
            headers: demoHeaders, credentials: 'include',
          });
          if (demoRes.ok) setDemo(await demoRes.json());
        } catch {}
      } catch {
        setError(t('Failed to load profile'));
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
    // initialProfile only seeds state; it never changes for a mounted view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameParam, currentUser, authLoading, router]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar />

      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 pb-28 md:pb-6">
        <div className="mb-4">
          <Link href="/home-feed" className="inline-flex items-center gap-2 text-sm transition-colors duration-100" style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}>
            <ArrowLeft size={15} />
            {t('Back to feed')}
          </Link>
        </div>

        <div className="flex flex-col xl:flex-row gap-6">
          {/* Main column */}
          <div className="flex-1 min-w-0 flex flex-col gap-5">
            {loading ? (
              <ProfileSkeleton />
            ) : error ? (
              <div className="card-base p-10 text-center" style={{ color: 'var(--text-muted)' }}>
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'var(--accent-muted)' }}
                >
                  <UserPlus size={28} style={{ color: 'var(--accent)' }} />
                </div>
                <p className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{isPrivate ? t('Private profile') : t('Profile not found')}</p>
                <p className="text-sm mb-6" style={{ maxWidth: 320, margin: '0 auto' }}>{error}</p>
                <div className="flex gap-3 justify-center">
                  <Link
                    href="/home-feed"
                    className="btn-ghost px-5 py-2.5 text-sm inline-block"
                  >
                    {t('Back to Feed')}
                  </Link>
                  {!currentUser && (
                    <Link
                      href="/sign-up-login-screen"
                      className="btn-primary px-5 py-2.5 text-sm inline-block"
                    >
                      {t('Sign In')}
                    </Link>
                  )}
                </div>
              </div>
            ) : profile ? (
              <>
                {/* Partner profile cover (renders only for partner accounts, e.g. fxdoctor) */}
                <PartnerHero username={profile.username} />
                <ProfileHeader
                  user={profile}
                  onFollowChange={(isFollowing, followerCount) => {
                    setProfile((prev) => prev ? { ...prev, isFollowing, stats: { ...prev.stats, followers: followerCount } } : prev);
                  }}
                />

                {profile.isCurrentUser && <AcademyBanner />}

                {/* Direct message — unified chat (same rooms as the mobile app) */}
                {!profile.isCurrentUser && currentUser && (
                  <button
                    onClick={async () => {
                      try {
                        const { getOrCreateDm } = await import('@/lib/liveChat');
                        const roomId = await getOrCreateDm(profile.userId);
                        window.location.href = `/messages?room=${roomId}`;
                      } catch (e: any) {
                        window.alert(e?.message || t('Could not open the conversation.'));
                      }
                    }}
                    className="w-full py-2.5 text-sm btn-ghost font-semibold"
                  >
                    💬 {t('Message')} @{profile.username}
                  </button>
                )}

                {/* Follow CTA for visitors who aren't following this trader */}
                {!profile.isCurrentUser && !profile.isFollowing && currentUser && (
                  <div
                    className="rounded-xl p-4 flex items-center justify-between gap-4 animate-fade-in"
                    style={{
                      background: 'var(--accent-muted)',
                      border: '1px solid var(--accent)',
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--bg-card)' }}>
                        <TrendingUp size={18} style={{ color: 'var(--accent)' }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {t('Follow')} @{profile.username} {t('to see their trades in your feed')}
                        </p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {profile.stats.winRate > 0 && (
                            <span className="text-xs font-mono" style={{ color: profile.stats.winRate >= 60 ? 'var(--bullish)' : 'var(--win)' }}>
                              {profile.stats.winRate}% {t('Win Rate')}
                            </span>
                          )}
                          {(profile.stats.performanceScore ?? 0) > 0 && (
                            <span className="text-xs font-mono" style={{ color: 'var(--info)' }}>
                              {(profile.stats.performanceScore ?? 0).toFixed(0)} pts
                            </span>
                          )}
                          {profile.stats.totalTrades > 0 && (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {profile.stats.totalTrades} {t('trades')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <ProfilePerformanceChart userId={profile.userId} />
                <ProfileTabs
                  userId={profile.userId}
                  username={profile.username}
                  bio={profile.bio}
                  stats={profile.stats}
                  achievements={achievements}
                  isCurrentUser={profile.isCurrentUser}
                  initialTab={searchParams.get('tab')}
                />
              </>
            ) : null}
          </div>

          {/* Sidebar */}
          <div className="w-full xl:w-72 2xl:w-80 shrink-0">
            <div className="sticky top-20 flex flex-col gap-4">
              {/* Live-Verified (real connected account) — shown on any profile that has it */}
              {demo?.scores?.verificationTier === 'LIVE_VERIFIED' && demo.scores.verifiedMetrics && (
                <div className="card-base p-4" style={{ border: '1px solid var(--bullish)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                      <ShieldCheck size={15} style={{ color: 'var(--bullish)' }} /> {t('Live-Verified')}
                    </h3>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)', border: '1px solid var(--bullish)' }}>
                      LIVE · BETA
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: t('Verified Score'), value: demo.scores.verifiedScore != null ? Number(demo.scores.verifiedScore).toFixed(0) : '—' },
                      { label: t('Gain'), value: demo.scores.verifiedMetrics.gainPct != null ? `${Number(demo.scores.verifiedMetrics.gainPct).toFixed(1)}%` : '—' },
                      { label: t('Profit Factor'), value: demo.scores.verifiedMetrics.profitFactor != null ? Number(demo.scores.verifiedMetrics.profitFactor).toFixed(2) : '—' },
                      { label: t('Max Drawdown'), value: demo.scores.verifiedMetrics.maxDrawdownPct != null ? `${Number(demo.scores.verifiedMetrics.maxDrawdownPct).toFixed(0)}%` : '—' },
                      { label: t('Trades'), value: demo.scores.verifiedMetrics.nTrades ?? '—' },
                      { label: t('Period'), value: demo.scores.verifiedMetrics.spanWeeks != null ? `${Number(demo.scores.verifiedMetrics.spanWeeks).toFixed(0)}w` : '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-lg p-2" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                        <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</p>
                        <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] mt-3" style={{ color: 'var(--text-dim)' }}>
                    {t('Real connected account · read-only · verified history. Beta — equity %-based.')}
                  </p>
                </div>
              )}

              {/* Demo (simulated) performance — current user only. DemoAccount + DemoPosition are the
                  single source of truth for account/trades/win%, shown as soon as the account exists
                  (NOT gated on stmScore). The gambler-proof STM score/tier is a separate gated block. */}
              {demo?.account && (
                <div className="card-base p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Performance')}</h3>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid var(--win)' }}>
                      {t('SIMULATED')}
                    </span>
                  </div>

                  {/* Equity + % return */}
                  <div className="rounded-xl p-3 mb-3" style={{ background: 'var(--accent-muted)', border: '1px solid var(--accent)' }}>
                    <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('Demo equity')}</p>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: demo.account.currency || 'USD' }).format(Number(demo.account.equity))}
                      </p>
                      {demo.demoStats && (
                        <span className="text-xs font-mono font-semibold" style={{ color: (demo.demoStats.returnPct ?? 0) >= 0 ? 'var(--bullish)' : 'var(--bearish)' }}>
                          {(demo.demoStats.returnPct ?? 0) >= 0 ? '+' : ''}{demo.demoStats.returnPct ?? 0}%
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {t('Balance')} {new Intl.NumberFormat('en-US', { style: 'currency', currency: demo.account.currency || 'USD' }).format(Number(demo.account.balance))} · {t('from $10k start')}
                    </p>
                  </div>

                  {/* Demo trade stats (ungated — show whenever the account exists) */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: t('Closed Positions'), value: String(demo.demoStats?.closedTrades ?? 0) },
                      { label: t('Demo Win Rate'), value: `${demo.demoStats?.winRate ?? 0}%` },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-lg p-2" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                        <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</p>
                        <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Gambler-proof STM score + tier — SEPARATE, unlocks after enough settled trades */}
                  {demo.scores && Number(demo.scores.stmScore ?? 0) > 0 ? (
                    <div className="rounded-xl p-3 mt-3" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>{t('STM Score (risk-adjusted)')}</span>
                        {demo.scores.tier && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'var(--win-muted)', color: 'var(--win)' }}>
                            {String(demo.scores.tier).charAt(0) + String(demo.scores.tier).slice(1).toLowerCase()}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: 'STM', value: Number(demo.scores.stmScore).toFixed(0) },
                          { label: t('Expectancy'), value: demo.scores.expectancyR != null ? `${Number(demo.scores.expectancyR).toFixed(2)}R` : '—' },
                          { label: 'PF', value: demo.scores.profitFactor != null ? Number(demo.scores.profitFactor).toFixed(2) : '—' },
                        ].map(({ label, value }) => (
                          <div key={label}>
                            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</p>
                            <p className="text-sm font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</p>
                          </div>
                        ))}
                      </div>
                      {(demo.scores.flags ?? []).length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {(demo.scores.flags ?? []).map((f: string) => (
                            <span key={f} className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: f === 'BLOWN' ? 'var(--bearish-muted)' : 'var(--win-muted)', color: f === 'BLOWN' ? 'var(--bearish)' : 'var(--win)' }}>
                              {f.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs mt-3" style={{ color: 'var(--text-dim)' }}>
                      {t('STM Score (risk-adjusted rank) unlocks after enough settled trades.')}
                    </p>
                  )}
                  <p className="text-[10px] mt-3" style={{ color: 'var(--text-dim)' }}>
                    {t('Simulated results from a $10k demo account — not real-money performance.')}
                  </p>
                </div>
              )}

              {/* Market Focus */}
              <div className="card-base p-4">
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{t('Market Focus')}</h3>
                {marketFocus.length === 0 ? (
                  <p className="text-xs" style={{ color: 'var(--text-dim)' }}>{t('No trade data yet')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {marketFocus.slice(0, 5).map((m) => {
                      const color = MARKET_COLORS[m.market] || 'var(--accent)';
                      return (
                        <div key={m.market}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium" style={{ color }}>{m.market}</span>
                            <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{m.pct}%</span>
                          </div>
                          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-sunken)' }}>
                            <div className="h-full rounded-full" style={{ width: `${m.pct}%`, background: color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
}